import React, { useState, forwardRef } from 'react';
import TimetableEntryModal from './TimetableEntryModal';
import styles from './TimetableGrid.module.css';

const TimetableGrid = forwardRef(({ data, configuration, onEntryEdit, onEntryDelete, onEntryCreate }, ref) => {
  const [selectedCell, setSelectedCell] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedEntry, setSelectedEntry] = useState(null);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].slice(0, configuration?.daysPerWeek || 6);
  const periods = configuration?.periods || [];
  
  const getEntries = (dayIndex, periodNumber) => {
    if (!data?.timetableGrid) return [];
    return data.timetableGrid[dayIndex + 1]?.[periodNumber] || [];
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const handleCellClick = (dayIndex, periodNumber) => {
    const entries = getEntries(dayIndex, periodNumber);
    
    if (entries.length === 0) {
      // Create new entry
      setModalMode('create');
      setSelectedCell({ dayOfWeek: dayIndex + 1, periodNumber });
      setSelectedEntry(null);
      setModalOpen(true);
    } else if (entries.length === 1) {
      // Edit single entry
      setModalMode('edit');
      setSelectedEntry(entries[0]);
      setSelectedCell(null);
      setModalOpen(true);
    } else {
      // Multiple entries - show selection
      setModalMode('edit');
      setSelectedEntry(entries[0]); // Edit first one for now
      setSelectedCell(null);
      setModalOpen(true);
    }
  };

  const handleEntryClick = (entry, e) => {
    e.stopPropagation();
    setModalMode('edit');
    setSelectedEntry(entry);
    setSelectedCell(null);
    setModalOpen(true);
  };

  const handleDelete = async (entryId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this entry?')) {
      await onEntryDelete(entryId);
    }
  };

  const handleModalSave = async (data) => {
    if (modalMode === 'create') {
      await onEntryCreate(data);
    } else {
      await onEntryEdit(selectedEntry.id, data);
    }
    setModalOpen(false);
  };

  return (
    <>
      <div className={styles.gridContainer} ref={ref}>
        <table className={styles.timetableTable}>
          <thead>
            <tr>
              <th className={styles.timeHeader}>Time</th>
              {days.map(day => (
                <th key={day} className={styles.dayHeader}>{day}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {periods.map(period => (
              <React.Fragment key={period.id}>
                {period.isBreak ? (
                  <tr className={styles.breakRow}>
                    <td className={styles.timeCell}>
                      <div className={styles.breakTime}>
                        {formatTime(period.startTime)} - {formatTime(period.endTime)}
                      </div>
                      <div className={styles.breakLabel}>{period.periodName}</div>
                    </td>
                    <td colSpan={days.length} className={styles.breakCell}>
                      <div className={styles.breakContent}>☕ BREAK</div>
                    </td>
                  </tr>
                ) : (
                  <tr className={styles.periodRow}>
                    <td className={styles.timeCell}>
                      <div className={styles.periodTime}>
                        {formatTime(period.startTime)} - {formatTime(period.endTime)}
                      </div>
                      <div className={styles.periodLabel}>{period.periodName}</div>
                    </td>
                    {days.map((day, dayIndex) => {
                      const entries = getEntries(dayIndex, period.periodNumber);
                      return (
                        <td 
                          key={`${day}-${period.periodNumber}`}
                          className={styles.entryCell}
                          onClick={() => handleCellClick(dayIndex, period.periodNumber)}
                        >
                          {entries.length === 0 ? (
                            <div className={styles.emptyCell}>
                              <button className={styles.addButton}>+</button>
                            </div>
                          ) : (
                            <div className={styles.entriesWrapper}>
                              {entries.map((entry, idx) => (
                                <div 
                                  key={entry.id}
                                  className={styles.entry}
                                  style={{ 
                                    backgroundColor: `${entry.color}20`,
                                    borderLeft: `4px solid ${entry.color}`,
                                    marginBottom: idx < entries.length - 1 ? '8px' : '0'
                                  }}
                                  onClick={(e) => handleEntryClick(entry, e)}
                                >
                                  <div className={styles.entryContent}>
                                    <div className={styles.subjectName}>{entry.subjectName}</div>
                                    <div className={styles.teacherName}>{entry.teacherName}</div>
                                    <div className={styles.className}>
                                      {entry.className} - {entry.sectionName}
                                    </div>
                                    {entry.roomNumber && (
                                      <div className={styles.roomNumber}>Room: {entry.roomNumber}</div>
                                    )}
                                  </div>
                                  <button
                                    className={styles.deleteButton}
                                    onClick={(e) => handleDelete(entry.id, e)}
                                    title="Delete"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <TimetableEntryModal
          mode={modalMode}
          entry={selectedEntry}
          cell={selectedCell}
          configuration={configuration}
          onSave={handleModalSave}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
});

export default TimetableGrid;